import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WeighingForm from './WeighingForm';
import PendingWeighings from './PendingWeighings';
import { Pesée, PeséeCreate } from '@/types';

interface WeighingInterfaceProps {
  filterStatus?: string;
  pageTitle?: string;
  pageDescription?: string;
}

const WeighingInterface: React.FC<WeighingInterfaceProps> = ({ 
  filterStatus, 
  pageTitle = "Gestion des Pesées",
  pageDescription = "Gérez vos pesées en cours"
}) => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [selectedWeighing, setSelectedWeighing] = useState<Pesée | null>(null);
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [observationText, setObservationText] = useState('');

  const handleNewWeighing = () => {
    // Rediriger vers la page NouvellePeseePage
    navigate('/nouvelle-pesee');
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setSelectedWeighing(null);
  };

  const handleSubmitWeighing = (data: PeséeCreate) => {
    console.log('Nouvelle pesée créée:', data);
    setShowForm(false);
    // Ici vous pouvez ajouter une logique pour rafraîchir la liste des pesées
  };

  const handleSelectWeighing = (weighing: Pesée) => {
    setSelectedWeighing(weighing);
    setShowForm(false);
    // Ici vous pouvez ajouter une logique pour éditer la pesée sélectionnée
  };

  // Fonction pour continuer une pesée
  const handleContinueWeighing = (weighing: Pesée) => {
    // Déterminer l'étape cible selon le statut
    let targetStep = 1; // Par défaut, étape 1
    
    switch (weighing.statut) {
      case 'EN_COURS':
        targetStep = 1; // Étape 1 - Pesée brut
        break;
      case 'BRUT_ENREGISTRE':
        targetStep = 2; // Étape 2- Annalyse qualité 
        break;
      case 'EN_ATTENTE_TARE':
        targetStep = 3; // Étape 3 - Pesée tare
        break;
      case 'TARE_ENREGISTREE':
        targetStep = 4; // Étape 4 - Ticket final
        break;
      default:
        targetStep = 1; // Par défaut, étape 1
        break;
    }
    
    // Rediriger vers la page de pesée avec l'ID de la pesée et l'étape cible
    navigate(`/nouvelle-pesee?weighingId=${weighing.id}&step=${targetStep}`);
  };

  // Fonction pour gérer l'ajout d'observation
  const handleAddObservation = () => {
    setObservationText(selectedWeighing?.observations || '');
    setShowObservationModal(true);
  };

  // Fonction pour sauvegarder l'observation
  const handleSaveObservation = async () => {
    if (!selectedWeighing || !observationText.trim()) return;
    
    try {
      // Ici vous pouvez ajouter l'appel API pour sauvegarder l'observation
      // Mettre à jour l'observation localement
      setSelectedWeighing({
        ...selectedWeighing,
        observations: observationText
      });
      
      setShowObservationModal(false);
      setObservationText('');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'observation:', error);
    }
  };

  // Fonction pour formater le statut d'une pesée
  const getWeighingStatusInfo = (statut: string) => {
    switch (statut) {
      case 'COMPLETEE':
        return { 
          label: 'Complétée', 
          color: 'green', 
          bgColor: 'bg-green-100', 
          textColor: 'text-green-800',
          icon: 'fas fa-check-circle'
        };
      case 'EN_COURS':
        return { 
          label: 'En cours', 
          color: 'blue', 
          bgColor: 'bg-blue-100', 
          textColor: 'text-blue-800',
          icon: 'fas fa-play-circle'
        };
      case 'EN_ATTENTE_TARE':
        return { 
          label: 'En attente de tare', 
          color: 'yellow', 
          bgColor: 'bg-yellow-100', 
          textColor: 'text-yellow-800',
          icon: 'fas fa-clock'
        };
      case 'TARE_ENREGISTREE':
        return { 
          label: 'Tare enregistrée', 
          color: 'purple', 
          bgColor: 'bg-purple-100', 
          textColor: 'text-purple-800',
          icon: 'fas fa-weight-hanging'
        };
      case 'BRUT_ENREGISTRE':
        return { 
          label: 'Brut enregistré', 
          color: 'blue', 
          bgColor: 'bg-blue-100', 
          textColor: 'text-blue-800',
          icon: 'fas fa-weight'
        };
      case 'REFOULEE':
        return { 
          label: 'Refoulée', 
          color: 'red', 
          bgColor: 'bg-red-100', 
          textColor: 'text-red-800',
          icon: 'fas fa-times-circle'
        };
      case 'ANNULEE':
        return { 
          label: 'Annulée', 
          color: 'gray', 
          bgColor: 'bg-gray-100', 
          textColor: 'text-gray-800',
          icon: 'fas fa-ban'
        };
      default:
        return { 
          label: statut, 
          color: 'gray', 
          bgColor: 'bg-gray-100', 
          textColor: 'text-gray-800',
          icon: 'fas fa-question-circle'
        };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {pageTitle}
              </h1>
              <p className="text-gray-600 mt-2">
                {pageDescription}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-gray-500">Date</div>
                <div className="font-medium">
                  {new Date().toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </div>
              </div>
              <button
                onClick={handleNewWeighing}
                className={`px-6 py-3 text-white rounded-lg focus:outline-none focus:ring-2 font-medium transition-colors shadow-md hover:shadow-lg ${
                  filterStatus === 'BRUT_ENREGISTRE' 
                    ? 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500' 
                    : 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
                }`}
              >
                <i className="fas fa-plus mr-2"></i>
                Nouvelle Pesée
              </button>
            </div>
          </div>
        </div>

        {/* Interface principale */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne gauche - Liste des pesées en attente */}
          <div className="lg:col-span-1">
            <PendingWeighings 
              onSelectWeighing={handleSelectWeighing} 
              filterStatus={filterStatus}
            />
          </div>

          {/* Colonne droite - Formulaire ou détails */}
          <div className="lg:col-span-2">
            {showForm ? (
              <WeighingForm
                onSubmit={handleSubmitWeighing}
                onCancel={handleCancelForm}
              />
            ) : selectedWeighing ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800">
                      Détails de la Pesée
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Référence: {selectedWeighing.reference}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    {/* Statut mis en évidence */}
                    {(() => {
                      const statusInfo = getWeighingStatusInfo(selectedWeighing.statut);
                      return (
                        <div className={`px-3 py-2 rounded-lg ${statusInfo.bgColor} ${statusInfo.textColor} flex items-center`}>
                          <i className={`${statusInfo.icon} mr-2`}></i>
                          <span className="font-semibold">{statusInfo.label}</span>
                        </div>
                      );
                    })()}
                    <button
                      onClick={() => setSelectedWeighing(null)}
                      className="text-gray-500 hover:text-gray-700 p-1"
                    >
                      <i className="fas fa-times text-xl"></i>
                    </button>
                  </div>
                </div>
                
                {/* Informations principales */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                  {/* Poids */}
                  <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
                    <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                      <i className="fas fa-weight mr-2"></i>
                      Poids
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-green-700">Brut:</span>
                        <span className="font-semibold text-green-800">
                          {selectedWeighing.poids_brut ? `${parseFloat(selectedWeighing.poids_brut.toString()).toLocaleString('fr-FR')} kg` : ''}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-green-700">Tare:</span>
                        <span className="font-semibold text-green-800">
                          {selectedWeighing.poids_tare ? `${parseFloat(selectedWeighing.poids_tare.toString()).toLocaleString('fr-FR')} kg` : ''}
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-green-300 pt-2">
                        <span className="text-sm font-semibold text-green-800">Net:</span>
                        <span className="font-bold text-lg text-green-900">
                          {selectedWeighing.poids_net ? `${parseFloat(selectedWeighing.poids_net.toString()).toLocaleString('fr-FR')} kg` : ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Véhicule et Transport */}
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
                      <i className="fas fa-truck mr-2"></i>
                      Véhicule & Transport
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-blue-700">Camion:</span>
                        <p className="font-semibold text-blue-800">{selectedWeighing.camion_immatriculation}</p>
                      </div>
                      <div>
                        <span className="text-sm text-blue-700">Chauffeur:</span>
                        <p className="font-semibold text-blue-800">{selectedWeighing.chauffeur_nom}</p>
                      </div>
                      <div>
                        <span className="text-sm text-blue-700">Transporteur:</span>
                        <p className="font-semibold text-blue-800">{selectedWeighing.chauffeur_info?.nom || ''}</p>
                      </div>
                    </div>
                  </div>

                  {/* Produit et Partenaires */}
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4">
                    <h3 className="font-semibold text-orange-800 mb-3 flex items-center">
                      <i className="fas fa-box mr-2"></i>
                      Produit & Partenaires
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-orange-700">Produit:</span>
                        <p className="font-semibold text-orange-800">{selectedWeighing.produit_nom}</p>
                      </div>
                      <div>
                        <span className="text-sm text-orange-700">Fournisseur:</span>
                        <p className="font-semibold text-orange-800">{selectedWeighing.fournisseur_nom}</p>
                      </div>
                      <div>
                        <span className="text-sm text-orange-700">Client:</span>
                        <p className="font-semibold text-orange-800">{selectedWeighing.client_nom || ''}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Informations supplémentaires */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <i className="fas fa-info-circle mr-2"></i>
                      Informations Générales
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-600">Date/Heure:</span>
                        <p className="font-semibold text-gray-800">
                          {selectedWeighing.date_heure ? new Date(selectedWeighing.date_heure).toLocaleString('fr-FR') : ''}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Type:</span>
                        <p className="font-semibold text-gray-800">{selectedWeighing.type_pesee_display || ''}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Provenance:</span>
                        <p className="font-semibold text-gray-800">{selectedWeighing.provenance || ''}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Destination:</span>
                        <p className="font-semibold text-gray-800">{selectedWeighing.destination || ''}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                      <i className="fas fa-file-alt mr-2"></i>
                      Documents & Références
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-600">N° Lot:</span>
                        <p className="font-semibold text-gray-800">{selectedWeighing.numero_lot || ''}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Connaissement:</span>
                        <p className="font-semibold text-gray-800">{selectedWeighing.connaissement || ''}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">N° Transfert:</span>
                        <p className="font-semibold text-gray-800">{selectedWeighing.numero_transfert || ''}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Sticker:</span>
                        <p className="font-semibold text-gray-800">{selectedWeighing.sticker || ''}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Observations */}
                {selectedWeighing.observations && (
                  <div className="bg-yellow-50 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-yellow-800 mb-2 flex items-center">
                      <i className="fas fa-comment mr-2"></i>
                      Observations
                    </h3>
                    <p className="text-yellow-800">{selectedWeighing.observations}</p>
                  </div>
                )}

                {/* Boutons d'action */}
                <div className="flex flex-wrap gap-3">
                  <button 
                    onClick={() => handleContinueWeighing(selectedWeighing)}
                    className={`px-6 py-3 text-white rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center font-medium ${
                      filterStatus === 'BRUT_ENREGISTRE' 
                        ? 'bg-indigo-600 hover:bg-indigo-700' 
                        : 'bg-orange-600 hover:bg-orange-700'
                    }`}
                  >
                    <i className="fas fa-play mr-2"></i>
                    Continuer la pesée
                  </button>
                  <button 
                    onClick={handleAddObservation}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg flex items-center font-medium"
                  >
                    <i className="fas fa-comment mr-2"></i>
                    Ajouter Observation
                  </button>
                  <button className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md hover:shadow-lg flex items-center font-medium">
                    <i className="fas fa-times mr-2"></i>
                    Refuser
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucune pesée sélectionnée
                </h3>
                <p className="text-gray-500 mb-6">
                  Sélectionnez une pesée dans la liste de gauche ou créez une nouvelle pesée
                </p>
                <button
                  onClick={handleNewWeighing}
                  className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium transition-colors shadow-md hover:shadow-lg"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Créer une Pesée
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal pour ajouter une observation */}
      {showObservationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Ajouter une observation</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Pesée: {selectedWeighing?.reference}
                  </p>
                </div>
                <button
                  onClick={() => setShowObservationModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="fas fa-comment mr-2"></i>
                    Observation
                  </label>
                  <textarea
                    value={observationText}
                    onChange={(e) => setObservationText(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                    placeholder="Saisissez votre observation..."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {observationText.length} caractères
                  </p>
                </div>

                {/* Aperçu de l'observation */}
                {observationText && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Aperçu:</h4>
                    <p className="text-gray-700 whitespace-pre-wrap">{observationText}</p>
                  </div>
                )}
              </div>

              {/* Boutons d'action */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowObservationModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveObservation}
                  disabled={!observationText.trim()}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <i className="fas fa-save mr-2"></i>
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeighingInterface;
